import React from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useGrantSignupByEmail } from 'hooks/api/useGrantSignup'
import { Controller, useForm } from 'react-hook-form'

import { Stack, Typography } from '@mui/material'
import { Modal, Button, TextField } from '@repo/ui'

interface GrantSignupModalProps {
  open: boolean
  onClose: () => void
}

type FormData = {
  email: string
  name: string
}

/**
 * VRG-92 / VRG-134: Header-level "Grant access" modal.
 * Accepts an email + the person's full name and calls POST /internal/signups/grant-email.
 * The email is allowlisted and a self-serve signup link is emailed to the person. They create
 * their company themselves on the signup page. No company is provisioned here.
 */
export const GrantSignupModal = ({ open, onClose }: GrantSignupModalProps) => {
  const { open: notification } = useSnackbar()
  const methods = useForm<FormData>({
    defaultValues: { email: '', name: '' },
  })
  const { control, handleSubmit, reset } = methods

  const { mutate: grantByEmail, isPending } = useGrantSignupByEmail({
    onSuccess: (_, variables) => {
      notification({
        type: 'success',
        title: `Signup link emailed to ${variables.email}`,
      })
      handleClose()
    },
    onError: (error) => {
      notification({
        type: 'error',
        title: getErrorMessage(error),
      })
    },
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = handleSubmit((data) => {
    // "Invite" = Allow & invite: allowlist + email a self-serve signup link (VRG-133/VRG-134).
    grantByEmail({
      email: data.email.trim(),
      name: data.name.trim() || undefined,
      invite: true,
    })
  })

  return (
    <Modal
      title="Grant access"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="grant-signup-form"
            loading={isPending}
          >
            Grant access
          </Button>
        </>
      }
    >
      <Stack component="form" id="grant-signup-form" onSubmit={onSubmit} spacing={4} mt={2}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Enter the person's email and name to grant access. We allowlist the email and send them a
          self-serve signup link. They set up their company themselves when they sign up.
        </Typography>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Email
          </Typography>
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Email is required',
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Enter a valid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                placeholder="founder@acme.com"
                type="email"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontSize: 16 }}>
            Full name <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography>
          </Typography>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Jane Founder"
              />
            )}
          />
        </Stack>
      </Stack>
    </Modal>
  )
}
